import { Button, Checkbox, Col, Form, Input, Radio, Row, Select, Spin, Tag } from 'antd';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import evaluationTypes from "../../../utils/evaluationType.json";
import { usePatientFormPE } from '../hooks/usePatientFormPE';
import PersonalDataBlock from './PatientFormPE/PersonalDataBlock';
import JobDataBlock from './PatientFormPE/JobDataBlock';
import OvercapacityModal from './PatientFormPE/OvercapacityModal';
import PrestationsDataBlock from './PatientFormPE/PrestationsDataBlock';

const { Option } = Select;
const { format } = require('rut.js');

const RequiredLabel = ({ children }) => (
  <span>
    <span className="required-asterisk">*</span>
    {children}
  </span>
);

const PatientFormPE = ({
  index,
  patient,
  updatePatientAttribute,
  updatePatientServices,
  updatePatientPrestations,
  repeatServicesForAllPatients,
  repeatLastPatientServices,
  patients,
  totalPatients,
  type,
  reservationForm,
  repeatBlocksForAllPatients,
  setDisabled,
  countrySettings,
  projects,
  setProjects,
  areasList,
  jobPositionsList,
  handleAreaCreated,
  handleJobPositionCreated,
}) => {
  const {
    t,
    form,
    overCapacityForm,
    loading,
    overCapacityLoading,
    loadingServices,
    loadingProjects,
    isOverCapacityModalVisible,
    services,
    blocks,
    documentType,
    genders,
    isProfessionEditable,
    setDocumentType,
    handleOnBlur,
    onFieldsChange,
    fetchServices,
    fetchProjects,
    projects: projectsFetched,
    handleOnChangeSearch,
    handleCancel,
    fetchBlocks,
    handleRepeatBlocksForAll,
    handleBlock,
    submitOverCapacity,
    capacityByBlock,
    searchPatient,
    setServices,
    currentBlock,
    loadingPatient,
  } = usePatientFormPE({
    index,
    patient,
    patients,
    reservationForm,
    updatePatientAttribute,
    updatePatientServices,
    repeatBlocksForAllPatients,
    setDisabled,
    countrySettings,
    jobPositionsList,
  });

  useEffect(() => {
    if (patient) {
      setServices(prev => {
        const prestationServices = patient.prestations.map(item => ({
          id: item.id,
          name: item.name,
          sale_amount: item.sale_amount,
          locateCode: item.locate_code,
          currencyCode: item.currencyCode,
          countryCode: item.countryCode,
          type: item.type,
          department_id: item.department_id,
          currency_id: item.currency_id,
          description: item.description,
          battery_exams: item.battery_exams || [],
          report_type: item.report_type || null,
          medical_form_type: item.medical_form_type || null,
          observations: item.observations || null,
        }));
        const prestationKeys = new Set(prestationServices.map(service => `${service.id}-${service.type}`));
        const searchResults = prev.filter(service => !prestationKeys.has(`${service.id}-${service.type}`));
        return [...prestationServices, ...searchResults];
      });
      form.setFieldsValue({
        'id': patient.id && patient.id,
        'block_id': patient.block_id ? patient.block_id : null,
        'block': patient.block ? patient.block : null,
        'phone': patient.phone ? patient.phone : '',
        'profession': isProfessionEditable ? (patient.profession ?? '') : '',
        'documentType': patient.document_type ? patient.document_type : documentType,
        'documentNumber': patient.document_type === 'rut' ? format(patient.document_number || '') : (patient.document_number || ''),
        'names': patient.names ? patient.names : '',
        'last_name': patient.last_name ? patient.last_name : '',
        'gender': (patient.gender !== null && patient.gender !== undefined) ? parseInt(patient.gender) : null,
        'birthdate': patient.birthdate ? moment(patient.birthdate) : null,
        'prestations': patient.prestations?.map(service => `${service.id}-${service.type}`),
        'evaluation_type': patient.evaluation_type,
        'email': patient.email ? patient.email : '',
        'external_id': patient.external_id ? patient.external_id : '',
        'project_id': patient.project_id,
        'paid_by_patient': patient.paid_by_patient,
        'job_position_id': patient.job_position_id ? patient.job_position_id : null,
        'area_id': patient.area_id ? patient.area_id : null,
        '_job_position_id': patient.job_position_id ? patient.job_position_id : null,
        '_area_id': patient.area_id ? patient.area_id : null,
      });
    } else {
      form.setFieldsValue({ 'documentType': documentType });
    }
  }, [patients, totalPatients, patient, form, documentType, isProfessionEditable, setServices]);

  const reservationDate = reservationForm.getFieldValue('reservation_date');
  const branchOfficeId = reservationForm.getFieldValue('branch_office_id');
  const reservationPrestations = reservationForm.getFieldValue('prestations');

  useEffect(() => {
    fetchBlocks();
  }, [reservationDate, branchOfficeId, reservationPrestations, fetchBlocks]);

  const updatePatientAttributeRef = useRef(updatePatientAttribute);
  useEffect(() => {
    updatePatientAttributeRef.current = updatePatientAttribute;
  }, [updatePatientAttribute]);

  useEffect(() => {
    if (!reservationForm.getFieldValue('id')) {
      updatePatientAttributeRef.current(index, 'prestations', []);
    }
  }, [branchOfficeId, index, reservationForm]);

  const evaluationTypeFormInput = (
    <Spin className='cmt-spin' spinning={loadingPatient.evaluationType}>
      <Form.Item
        className="m-0 p-0"
        name="evaluation_type"
        rules={[{ required: true, message: 'Tipo de evaluación es requerido' }]}
      >
        <Radio.Group id='evaluation_type' className="cmt-radio-left ant-radio-pe">
          {evaluationTypes["PE"]?.map((type) =>
            <Radio key={`${index}-${type.value}`} value={type.value}>{type.text}</Radio>
          )}
        </Radio.Group>
      </Form.Item>
    </Spin>
  );

  return (
    <Row>
      <Col lg={24} xs={24} className='patient-form-wrapper'>
        <OvercapacityModal
          t={t}
          isOverCapacityModalVisible={isOverCapacityModalVisible}
          handleCancel={handleCancel}
          currentBlock={currentBlock}
          overCapacityForm={overCapacityForm}
          submitOverCapacity={submitOverCapacity}
          overCapacityLoading={overCapacityLoading}
        />
        <Form form={form} layout="vertical" onFieldsChange={onFieldsChange} className="cmt-form show-required-marks">
          <Form.Item name="id" hidden>
            <Input type="hidden" />
          </Form.Item>

          <h3 className='mb-10'><strong>Información de la cita</strong></h3>
          <Row gutter={10} className={`${(index === 0 && totalPatients > 1 && patients[index]['block']) || 'mb-10'}`}>
            <Col lg={8} xs={24}>
              <Form.Item
                name="block_id"
                label={t('reservations.patientForm.blockLabel')}
                className='patient-form blocks select-wrapper mb-0'
                rules={[{ required: true, message: t('reservations.patientForm.blockRule') }]}
              >
                {loading
                  ? <Row justify="center"><Spin className="custom-spinner" /></Row>
                  : <Select onChange={handleBlock} className="cmt-select" placeholder={t('reservations.patientForm.blockPlaceholder')}>
                    {blocks?.map((block) => {
                      const capacity = capacityByBlock(block);
                      return (
                        <Option key={block.id} value={block.id} block={block}>
                          {block.block} <Tag color={capacity > 0 ? 'green' : 'red'}>{capacity < 0 ? 0 : capacity} cupos</Tag>
                        </Option>
                      );
                    })}
                  </Select>
                }
              </Form.Item>
            </Col>
            <Col lg={16} xs={24}>
              <Form.Item name="paid_by_patient" label='Pago realizado por el trabajador' className="mb-0 paid-by-patient" valuePropName="checked">
                <Checkbox className="cmt-checkbox" />
              </Form.Item>
            </Col>
          </Row>

          {(index === 0 && totalPatients > 1 && patients[index]['block']) && (
            <Row className='mb-10'>
              <Col lg={24} xs={24}>
                <Button onClick={() => handleRepeatBlocksForAll()} className="cmt-button secondary non-outline p-0 left-content h-auto">
                  <span className="mb-0 fs-14">{t('reservations.patientForm.repeatBlockForAll')}</span>
                </Button>
              </Col>
            </Row>
          )}

          <h3 className='mb-10'><strong><RequiredLabel>Tipo de evaluación</RequiredLabel></strong></h3>
          <Row gutter={20} className='mb-10'>
            <Col lg={24} xs={24} className='cmt-radio-container'>
              {evaluationTypeFormInput}
            </Col>
          </Row>

          <h3 className='mb-10'><strong>Datos personales</strong></h3>
          <PersonalDataBlock
            t={t}
            form={form}
            searchPatient={searchPatient}
            genders={genders}
            setDocumentType={setDocumentType}
            handleOnBlur={handleOnBlur}
          />

          <h3 className='mb-10'><strong>Datos del puesto</strong></h3>
          <JobDataBlock
            t={t}
            projects={projectsFetched}
            loadingProjects={loadingProjects}
            fetchProjects={fetchProjects}
            areasList={areasList}
            jobPositionsList={jobPositionsList}
            countrySettings={countrySettings}
            form={form}
            reservationForm={reservationForm}
            onFieldsChange={onFieldsChange}
            handleAreaCreated={handleAreaCreated}
            handleJobPositionCreated={handleJobPositionCreated}
          />

          <PrestationsDataBlock
            t={t}
            form={form}
            reservationForm={reservationForm}
            index={index}
            totalPatients={totalPatients}
            patients={patients}
            countrySettings={countrySettings}
            loadingServices={loadingServices}
            services={services}
            setServices={setServices}
            fetchServices={fetchServices}
            handleOnChangeSearch={handleOnChangeSearch}
            repeatServicesForAllPatients={repeatServicesForAllPatients}
            repeatLastPatientServices={repeatLastPatientServices}
            updatePatientPrestations={updatePatientPrestations}
          />

        </Form>
      </Col>
    </Row>
  );
};

export default PatientFormPE;
